import MaskToggle from './MaskToggle';
import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Crosshair, Check, X, Route } from 'lucide-react';
import { haversine } from '../lib/model';
import { extRtk, measurePrecise } from '../lib/precision';
import { subscribeGnss, gnssLive, gnssState, averageGnss, gnssMeta, getGnssCfg, FIX_SHORT } from '../lib/gnss';

const AUTO = [0, 1, 2, 5, 10];
const prof = (g) => ({ lat: g.lat, lon: g.lon, ground: g.ground, pipe: g.pipe, fix: g.fix, acc: g.acc, sats: g.sats, hdop: g.hdop });

// walk a pipe with the receiver: manual vertex or auto every X m
export default function GnssLineBar({ t, net, onClose }) {
  const [auto, setAuto] = useState(0);
  const [busy, setBusy] = useState(false);
  const d = net.draft;
  const lastRef = useRef(null);
  const n = d?.kind === 'pipe' ? d.points.length : 0;
  const add = (g) => { net.addPoint([g.lat, g.lon], undefined, { prof: prof(g) }); lastRef.current = [g.lat, g.lon]; };
  useEffect(() => {
    if (!auto) return undefined;
    return subscribeGnss((s) => {
      if (!gnssLive(s) || !net.draftRef.current) return;
      const last = lastRef.current;
      if (!last || haversine(last, [s.lat, s.lon]) >= auto) add(gnssMeta({ ...s, epochs: 1 }));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auto]);
  const measure = async () => {
    if (!gnssLive(gnssState())) return toast.error(t.gnssNoFix);
    setBusy(true);
    if (extRtk()) { const r = await measurePrecise(); setBusy(false); if (r) add(r.gnss); return; }
    const g = gnssMeta(await averageGnss(+getGnssCfg().avg));
    setBusy(false);
    toast.warning(t.prNoRtk, { id: 'prec-warn' });
    add(g);
  };
  const s = gnssState();
  return (
    <div className="gnss-line-bar mask" data-testid="gnss-line-bar">
      <MaskToggle t={t} id="gnss-line" />
      <b className="mask-keep"><Route size={14} /> {t.glTitle} · <span data-testid="gnss-line-count">{n}</span> {t.glPoints}</b>
      <small>{FIX_SHORT[s.fix || 'none']} · ± {s.hrms != null ? s.hrms.toFixed(3) : '–'} m</small>
      <div className="row">
        <button className="btn btn-accent btn-sm" data-testid="gnss-line-measure-btn" disabled={busy} onClick={measure}><Crosshair size={14} /> {t.glMeasure}</button>
        <label className="gl-auto">{t.glAuto}
          <select data-testid="gnss-line-auto-select" value={auto} onChange={(e) => { setAuto(+e.target.value); lastRef.current = d?.points?.length ? d.points[d.points.length - 1] : null; }}>
            {AUTO.map((m) => <option key={m} value={m}>{m ? `${m} m` : t.glAutoOff}</option>)}
          </select>
        </label>
        <button className="btn btn-green btn-sm" data-testid="gnss-line-finish-btn" disabled={n < 2} onClick={() => { net.finishPipe(); onClose(); }}><Check size={14} /> {t.glFinish}</button>
        <button className="icon-btn" data-testid="gnss-line-cancel-btn" title={t.glCancel} onClick={() => { net.cancelDraft(); onClose(); }}><X size={15} /></button>
      </div>
    </div>
  );
}
